import { graphHooks } from '@tunasong/graph-lib/react'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useActionToast } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { MovePickerDialog } from './move-picker-dialog.js'
import type { Trail } from './move-picker-state.js'
import { destinationId, destinationName, itemsToMove, moveFailedText, movedText } from './move-picker-state.js'
import { closeMovePicker, useMovePickerRequest } from './move-picker-store.js'
import { useMoveRecents } from './use-move-recents.js'

/** Where an item was before the move, for Undo: `null` is Unfiled */
const previousParent = (item: Persisted<Entity>) => (!item.parentId || item.parentId === 'ROOT' ? null : item.parentId)

/**
 * The Move picker and what it says afterwards (#2543). Mounted once, beside the app's alerts: the menu that opens it
 * closes, and the moved row may leave its list, but the Undo toast must stay.
 *
 * The move is awaited (P1-2): `Moved "{name}" to {folder}.` with Undo only once it happened; a failure shows only
 * `Couldn't move "{name}". Nothing changed.`, with Try again.
 */
export const MovePickerHost: FC = () => {
  const request = useMovePickerRequest()
  const { userId } = useCurrentUser()
  const { moveEntityTo } = graphHooks.useEntityUtil()
  const { showToast, toast } = useActionToast()
  const { remember } = useMoveRecents()

  /** Each move to its parent; the ones that did not happen */
  const moveAll = async (moves: { item: Persisted<Entity>; parentId: string | null }[]) => {
    const done = await Promise.all(moves.map(({ item, parentId }) => moveEntityTo({ entityId: item.id, parentId })))
    return moves.filter((_, index) => !done[index])
  }

  const undo = async (moved: Persisted<Entity>[]) => {
    const failed = await moveAll(moved.map(item => ({ item, parentId: previousParent(item) })))
    if (failed.length > 0) {
      showToast({
        message: moveFailedText(failed.map(({ item }) => item)),
        actionLabel: 'Try again',
        severity: 'error',
        onAction: () => void undo(failed.map(({ item }) => item)),
      })
    }
  }

  const move = async (items: Persisted<Entity>[], trail: Trail) => {
    const parentId = destinationId(trail)
    const failed = await moveAll(items.map(item => ({ item, parentId })))
    if (failed.length > 0) {
      const failedItems = failed.map(({ item }) => item)
      showToast({
        message: moveFailedText(failedItems),
        actionLabel: 'Try again',
        severity: 'error',
        onAction: () => void move(failedItems, trail),
      })
      return
    }
    /** Recent (#2543 M2): only a move that happened */
    remember(parentId)
    showToast({
      message: movedText(items, destinationName(trail)),
      actionLabel: 'Undo',
      onAction: () => void undo(items),
    })
  }

  const handleMove = (trail: Trail) => {
    if (!request) {
      return
    }
    closeMovePicker()
    void move(itemsToMove({ items: request.items, trail, userId }), trail)
  }

  return (
    <>
      {request ? <MovePickerDialog items={request.items} onMove={handleMove} onCancel={closeMovePicker} /> : null}
      {toast}
    </>
  )
}
